import { useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { setUpFirstAdministrator } from "../../api/accountsApi";
import { appPaths } from "../../shared/routing/appPaths";
import ErrorNotice from "../../errors/ErrorNotice";
import { useSession } from "../../session/sessionContext";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import StatusMessage from "../../ui/feedback/StatusMessage";
import { FORM_CONTROL_CLASS } from "../../ui/styles/formStyles";
import AuthCardPage from "./AuthCardPage";
import BackToSignInLink from "./BackToSignInLink";
import NewPasswordFields from "./NewPasswordFields";
import Field from "../../ui/forms/Field";

/**
 * `/setup`: a fresh installation creates its first administrator once, with the setup token
 * from the server's configuration (`accounts.first_administrator_setup_token`).
 */
export default function FirstAdministratorSetupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signIn } = useSession();
  const { state, submit } = useSubmission();
  const [setupToken, setSetupToken] = useState("");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [mismatch, setMismatch] = useState(false);

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password !== confirmation) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    submit(async () => {
      const administrator = await setUpFirstAdministrator({ setupToken, email, displayName, password });
      await signIn({ email: administrator.email, password });
      await navigate(appPaths.projects(), { replace: true });
    });
  };

  return (
    <AuthCardPage title={t("setup.title")}>
      <p className="app-text-muted mb-4 text-sm">{t("setup.intro")}</p>
      <form onSubmit={handleSubmit}>
        <fieldset disabled={state.status === "submitting"}>
          <Field id="setup-token" label={t("setup.token")}>
            {(control) => (
              <input
                {...control}
                type="password"
                className={`mb-1 ${FORM_CONTROL_CLASS}`}
                value={setupToken}
                onChange={(event) => {
                  setSetupToken(event.target.value);
                }}
                autoComplete="off"
                required
              />
            )}
          </Field>
          <p className="app-text-muted mb-3 text-xs">{t("setup.tokenHint")}</p>
          <Field id="setup-email" label={t("newAccount.email")}>
            {(control) => (
              <input
                {...control}
                type="email"
                className={`mb-3 ${FORM_CONTROL_CLASS}`}
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
                autoComplete="email"
                required
              />
            )}
          </Field>
          <Field id="setup-name" label={t("newAccount.displayName")}>
            {(control) => (
              <input
                {...control}
                className={`mb-3 ${FORM_CONTROL_CLASS}`}
                value={displayName}
                onChange={(event) => {
                  setDisplayName(event.target.value);
                }}
                autoComplete="name"
                required
              />
            )}
          </Field>
          <NewPasswordFields
            idPrefix="setup"
            password={password}
            confirmation={confirmation}
            onPasswordChange={setPassword}
            onConfirmationChange={setConfirmation}
          />
          {mismatch ? <StatusMessage tone="error" className="mb-3">{t("newAccount.passwordMismatch")}</StatusMessage> : null}
          {state.status === "failed" ? <ErrorNotice error={state.error} className="mb-3" /> : null}
          <AppButton type="submit" className="w-full" variant="primary">
            {state.status === "submitting" ? t("login.pleaseWait") : t("setup.submit")}
          </AppButton>
        </fieldset>
      </form>
      <BackToSignInLink />
    </AuthCardPage>
  );
}
